import type { childPartV2Param } from "@/api/breakdownService";
import { isValidDecimalInput, parseDecimal } from "@/utils/inputParse";
import { Box, Switch, TableCell, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useCalculateTotalMaterialCost } from "@/hooks/useBreakdownLogic";

interface MaterialCostDataEditProps {
  isOpenSecondTierProcessColumn: boolean;
  secondTierProcessColumn: { id: number; name: string }[];
  isOpenOtherProcessColumn: boolean;
  otherProcessColumn: { id: number; name: string }[];
  newChildPart: childPartV2Param | null;
  setNewChildPart: React.Dispatch<
    React.SetStateAction<childPartV2Param | null>
  >;
  isAdminPct: boolean;
  isProfitPct: boolean;
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const MaterialCostDataEdit = ({
  newChildPart,
  setNewChildPart,
  isOpenSecondTierProcessColumn,
  secondTierProcessColumn,
  isOpenOtherProcessColumn,
  otherProcessColumn,
  isAdminPct,
  isProfitPct,
  setFlaggedReason,
}: MaterialCostDataEditProps) => {
  const [adminInput, setAdminInput] = useState<string>(
    Number(newChildPart?.admin ?? 0).toFixed(2),
  );
  const [profitInput, setProfitInput] = useState<string>(
    Number(newChildPart?.profit ?? 0).toFixed(2),
  );
  const [percentInput, setPercentInput] = useState<string>(
    Number(newChildPart?.percent ?? 0).toFixed(2),
  );
  const [materialCostInput, setMaterialCostInput] = useState<string>(
    Number(newChildPart?.material_cost ?? 0).toFixed(2),
  );
  const [secondTierProcessCostInputs, setSecondTierProcessCostInputs] =
    useState<{ [key: number]: string }>({});
  const [otherProcessCostInputs, setOtherProcessCostInputs] = useState<{
    [key: number]: string;
  }>({});

  useEffect(() => {
    setAdminInput(Number(newChildPart?.admin ?? 0).toFixed(2));
  }, [newChildPart?.admin]);

  useEffect(() => {
    setProfitInput(Number(newChildPart?.profit ?? 0).toFixed(2));
  }, [newChildPart?.profit]);

  useEffect(() => {
    setPercentInput(Number(newChildPart?.percent ?? 0).toFixed(2));
  }, [newChildPart?.percent]);

  useEffect(() => {
    if (!newChildPart?.is_purchase_part) {
      setMaterialCostInput(Number(newChildPart?.material_cost ?? 0).toFixed(2));
    }
  }, [newChildPart?.material_cost, newChildPart?.is_purchase_part]);

  useEffect(() => {
    if (newChildPart?.child_part_material_second_tier_processes_v2) {
      const newInputs: { [key: number]: string } = {};
      newChildPart.child_part_material_second_tier_processes_v2.forEach((p) => {
        newInputs[p.part_material_second_tier_process_v2_id] = Number(
          p.cost ?? 0,
        ).toFixed(2);
      });
      setSecondTierProcessCostInputs(newInputs);
    }
  }, [newChildPart?.child_part_material_second_tier_processes_v2]);

  useEffect(() => {
    if (newChildPart?.child_part_material_other_processes_v2) {
      const newInputs: { [key: number]: string } = {};
      newChildPart.child_part_material_other_processes_v2.forEach((p) => {
        newInputs[p.part_material_other_process_v2_id] = Number(
          p.cost ?? 0,
        ).toFixed(2);
      });
      setOtherProcessCostInputs(newInputs);
    }
  }, [newChildPart?.child_part_material_other_processes_v2]);

  useEffect(() => {
    if (newChildPart?.is_purchase_part) {
      setAdminInput((0).toFixed(2));
      setProfitInput((0).toFixed(2));
      setPercentInput((0).toFixed(2));
      setSecondTierProcessCostInputs({});
      setOtherProcessCostInputs({});
      setNewChildPart((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          admin: 0,
          profit: 0,
          percent: 0,
          child_part_material_second_tier_processes_v2: [],
          child_part_material_other_processes_v2: [],
          second_tier_process_comment: "",
          other_process_comment: "",
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newChildPart?.is_purchase_part]);

  const {
    materialCost,
    returnScrapPrice,
    totalSecondTierProcessCost,
    totalOtherProcessCost,
    otherPercentCost,
    totalMaterialCost,
  } = useCalculateTotalMaterialCost(
    newChildPart as childPartV2Param,
    materialCostInput,
    isAdminPct,
    isProfitPct,
  );

  useEffect(() => {
    setNewChildPart((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        material_cost: parseDecimal(String(materialCost), 3),
        return_scrap_price: parseDecimal(String(returnScrapPrice), 3),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialCost, returnScrapPrice]);

  return (
    <>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={Number(totalMaterialCost ?? 0).toFixed(2)}
          disabled
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={materialCostInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setMaterialCostInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newMaterialCost = parseDecimal(e.target.value, 2);
            setMaterialCostInput(newMaterialCost.toFixed(2));
            setNewChildPart((prev) => {
              if (!prev) return null;
              return { ...prev, material_cost: newMaterialCost };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Material Cost");
              return newSet;
            });
          }}
          disabled={!newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 100,
          maxWidth: 100,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "center",
        }}
      >
        <Switch
          checked={newChildPart?.is_purchase_part}
          onChange={(e) => {
            setNewChildPart((prev) => {
              if (!prev) return null;
              return { ...prev, is_purchase_part: e.target.checked };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Purchase Part");
              return newSet;
            });
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={Number(returnScrapPrice ?? 0).toFixed(2)}
          disabled
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={adminInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setAdminInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newAdmin = parseDecimal(e.target.value, 2);
            setNewChildPart((prev) => {
              if (!prev) return null;
              return { ...prev, admin: newAdmin };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Admin");
              return newSet;
            });
          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={profitInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setProfitInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newProfit = parseDecimal(e.target.value, 2);
            setNewChildPart((prev) => {
              if (!prev) return null;
              return { ...prev, profit: newProfit };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Profit");
              return newSet;
            });
          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 180,
          maxWidth: 180,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
          <TextField
            variant="outlined"
            size="small"
            sx={{ "& input": { textAlign: "right" } }}
            value={(totalSecondTierProcessCost ?? 0).toFixed(2)}
            disabled
          />
          <TextField
            variant="outlined"
            size="small"
            sx={{ "& input": { textAlign: "right" } }}
            value={newChildPart?.second_tier_process_comment}
            onChange={(e) => {
              setNewChildPart((prev) => {
                if (!prev) return null;
                return { ...prev, second_tier_process_comment: e.target.value };
              });
            }}
            disabled={newChildPart?.is_purchase_part}
          />
        </Box>
      </TableCell>
      {isOpenSecondTierProcessColumn &&
        secondTierProcessColumn
          .sort((a, b) => a.id - b.id)
          .map((process) => (
            <TableCell
              key={process.id}
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                sx={{ "& input": { textAlign: "right" } }}
                value={
                  secondTierProcessCostInputs[process.id] ?? (0).toFixed(2)
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidDecimalInput(val, 2)) {
                    setSecondTierProcessCostInputs((prev) => ({
                      ...prev,
                      [process.id]: val.trim(),
                    }));
                  }
                }}
                onBlur={(e) => {
                  setNewChildPart((prev) => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      child_part_material_second_tier_processes_v2: [
                        ...(prev.child_part_material_second_tier_processes_v2?.filter(
                          (p) =>
                            p.part_material_second_tier_process_v2_id !==
                            process.id,
                        ) || []),
                        {
                          part_material_second_tier_process_v2_id: process.id,
                          cost: parseDecimal(e.target.value, 2),
                        },
                      ],
                    };
                  });
                  setFlaggedReason((prev) => {
                    const newSet = new Set(prev);
                    newSet.add("Second Tier");
                    return newSet;
                  });
                }}
                disabled={newChildPart?.is_purchase_part}
              />
            </TableCell>
          ))}
      <TableCell
        sx={{
          minWidth: 180,
          maxWidth: 180,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
          <TextField
            variant="outlined"
            size="small"
            sx={{ "& input": { textAlign: "right" } }}
            value={(totalOtherProcessCost ?? 0).toFixed(2)}
            disabled
          />
          <TextField
            variant="outlined"
            size="small"
            sx={{ "& input": { textAlign: "right" } }}
            value={newChildPart?.other_process_comment}
            onChange={(e) => {
              setNewChildPart((prev) => {
                if (!prev) return null;
                return { ...prev, other_process_comment: e.target.value };
              });
            }}
            disabled={newChildPart?.is_purchase_part}
          />
        </Box>
      </TableCell>
      {isOpenOtherProcessColumn &&
        otherProcessColumn
          .sort((a, b) => a.id - b.id)
          .map((process) => (
            <TableCell
              key={process.id}
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                sx={{ "& input": { textAlign: "right" } }}
                value={otherProcessCostInputs[process.id] ?? (0).toFixed(2)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidDecimalInput(val, 2)) {
                    setOtherProcessCostInputs((prev) => ({
                      ...prev,
                      [process.id]: val.trim(),
                    }));
                  }
                }}
                onBlur={(e) => {
                  setNewChildPart((prev) => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      child_part_material_other_processes_v2: [
                        ...(prev.child_part_material_other_processes_v2?.filter(
                          (p) =>
                            p.part_material_other_process_v2_id !== process.id,
                        ) || []),
                        {
                          part_material_other_process_v2_id: process.id,
                          cost: parseDecimal(e.target.value, 2),
                        },
                      ],
                    };
                  });
                  setFlaggedReason((prev) => {
                    const newSet = new Set(prev);
                    newSet.add("Others");
                    return newSet;
                  });
                }}
                disabled={newChildPart?.is_purchase_part}
              />
            </TableCell>
          ))}
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={percentInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setPercentInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newPercent = parseDecimal(e.target.value, 2);
            setNewChildPart((prev) => {
              if (!prev) return null;
              return { ...prev, percent: newPercent };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("%");
              newSet.add("Other (%)");
              return newSet;
            });
          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={Number(otherPercentCost ?? 0).toFixed(2)}
          disabled
        />
      </TableCell>
    </>
  );
};

export default MaterialCostDataEdit;
